import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Copy, Check, KeyRound } from "lucide-react";

export default function Security() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({
    queryKey: ["mfa-status"],
    queryFn: async () => (await api.get("/auth/mfa/status")).data,
  });

  const [setup, setSetup] = useState(null); // {secret, qr_data_url}
  const [enableCode, setEnableCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [copied, setCopied] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePw, setDisablePw] = useState("");

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetup(data); setEnableCode(""); setBackupCodes(null);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const confirmEnable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code: enableCode.trim() });
      setBackupCodes(data.backup_codes);
      setSetup(null);
      qc.invalidateQueries({ queryKey: ["mfa-status"] });
      toast.success("Two-factor authentication enabled");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const confirmDisable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: disablePw });
      setDisableOpen(false); setDisablePw(""); setBackupCodes(null);
      qc.invalidateQueries({ queryKey: ["mfa-status"] });
      toast.success("Two-factor authentication disabled");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText((backupCodes || []).join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const enabled = status?.enabled;

  return (
    <AppShell>
      <PageHeader title="Security" subtitle="Two-factor authentication" onBack={() => navigate("/more")} />
      <div className="px-5 pb-28 df-fade-in space-y-4">
        {/* Status card */}
        <div className="df-card p-5" data-testid="mfa-status-card">
          <div className="flex items-center gap-3">
            <span className={`h-11 w-11 rounded-full flex items-center justify-center ${enabled ? "bg-[#E6F4F1] text-[#0D786A]" : "bg-slate-100 text-slate-400"}`}>
              {enabled ? <ShieldCheck size={20} /> : <ShieldOff size={20} />}
            </span>
            <div>
              <div className="font-heading font-semibold text-slate-900">
                {isLoading ? "…" : enabled ? "Enabled" : "Not enabled"}
              </div>
              <div className="text-xs text-slate-500">
                {isLoading ? "Checking…" : enabled ? `${status?.backup_codes_remaining ?? 0} backup codes remaining` : "Add a code from your authenticator app at sign in"}
              </div>
            </div>
          </div>
        </div>

        {/* Backup codes just generated */}
        {backupCodes && (
          <div className="df-card p-5 border-2 border-[#0D786A]/30" data-testid="mfa-backup-codes">
            <div className="flex items-center gap-2 text-[#0D786A] font-semibold mb-1">
              <KeyRound size={16} /> Your backup codes
            </div>
            <p className="text-xs text-slate-500 mb-3">Save these somewhere safe. Each code works once if you lose your device. They won't be shown again.</p>
            <div className="grid grid-cols-2 gap-2 font-num text-sm">
              {backupCodes.map((c) => (
                <div key={c} className="bg-slate-50 rounded-lg px-3 py-2 text-center tracking-wider text-slate-800">{c}</div>
              ))}
            </div>
            <Button variant="outline" data-testid="mfa-copy-codes" onClick={copyCodes} className="w-full mt-3 rounded-xl">
              {copied ? <><Check size={16} className="mr-1.5" /> Copied</> : <><Copy size={16} className="mr-1.5" /> Copy codes</>}
            </Button>
          </div>
        )}

        {/* Setup flow */}
        {!enabled && setup && (
          <div className="df-card p-5 space-y-4" data-testid="mfa-setup-card">
            <div>
              <div className="font-heading font-semibold text-slate-900 mb-1">Scan the QR code</div>
              <p className="text-xs text-slate-500">Open Google Authenticator, Authy or 1Password and scan this code.</p>
            </div>
            <div className="flex justify-center">
              <img src={setup.qr_data_url} alt="MFA QR code" className="h-48 w-48 rounded-xl border border-slate-200" data-testid="mfa-qr" />
            </div>
            <div className="text-center text-xs text-slate-500">
              Or enter this key manually:
              <div className="font-num tracking-wider text-slate-800 mt-1 break-all" data-testid="mfa-secret">{setup.secret}</div>
            </div>
            <div>
              <Label className="text-slate-700">Enter the 6-digit code to confirm</Label>
              <Input data-testid="mfa-enable-code" inputMode="numeric" value={enableCode} maxLength={6}
                onChange={(e) => setEnableCode(e.target.value)} placeholder="123456"
                className="mt-1.5 h-12 rounded-xl bg-white font-num tracking-widest text-center" />
            </div>
            <Button data-testid="mfa-confirm-enable" onClick={confirmEnable} disabled={busy}
              className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
              {busy ? "Verifying…" : "Verify & enable"}
            </Button>
          </div>
        )}

        {/* Actions */}
        {!enabled && !setup && (
          <Button data-testid="mfa-start-setup" onClick={startSetup} disabled={busy}
            className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
            <ShieldCheck size={18} className="mr-1.5" /> Set up two-factor authentication
          </Button>
        )}
        {enabled && (
          <Button variant="outline" data-testid="mfa-disable-btn" onClick={() => setDisableOpen(true)}
            className="w-full h-12 rounded-xl border-red-200 text-red-600 hover:bg-red-50 df-pressable">
            <ShieldOff size={18} className="mr-1.5" /> Disable two-factor authentication
          </Button>
        )}

        <p className="text-center text-xs text-slate-400 pt-2">Two-factor authentication adds a second step at sign in using an app on your phone. It works offline — no SMS or email needed.</p>
      </div>

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Disable two-factor</DialogTitle>
            <DialogDescription>Confirm your password to turn off two-factor authentication. Your backup codes will stop working.</DialogDescription>
          </DialogHeader>
          <div className="mt-1">
            <Label className="text-slate-700">Password</Label>
            <Input type="password" data-testid="mfa-disable-password" value={disablePw}
              onChange={(e) => setDisablePw(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="mfa-disable-confirm" onClick={confirmDisable} disabled={busy}
              className="rounded-xl bg-red-600 hover:bg-red-700">Disable</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
